import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Container,
    Grid,
    Chip,
    TextField,
    MenuItem,
    Stack
} from "@mui/material";
import {
    TrendingUp,
    Download,
    FilterList,
    School
} from "@mui/icons-material";
import { useAuth } from "../../hooks/useAuth";
import AdminLayout from "../AdminLayout";
import learningService from "../../services/learningService";

export default function CreditHistoryPage() {
    const { userProfile } = useAuth();
    const [creditHistory, setCreditHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState("all");
    const [searchPath, setSearchPath] = useState("");

    useEffect(() => {
        const fetchCreditHistory = async () => {
            try {
                setLoading(true);

                if (!userProfile?.uid) return;

                // Fetch all learning paths for reference
                const allPaths = await learningService.getLearningPaths(false);
                const pathMap = {};
                allPaths.forEach((path) => {
                    pathMap[path.id] = path;
                });

                // Fetch student progress for all paths
                const progressSummary = await learningService.getStudentProgressSummary(
                    userProfile.uid
                );

                // Build credit history from completed paths
                const history = [];

                for (const progress of progressSummary) {
                    const path = pathMap[progress.pathId];
                    if (!path) continue;

                    const levelStatuses = progress.levelStatus || {};
                    const completedLevels = Object.entries(levelStatuses).filter(
                        ([_, status]) => status.cleared
                    );

                    // Add entry for each completed level
                    completedLevels.forEach(([levelId, levelStatus]) => {
                        const level = path.levels?.find((l) => l.id === levelId);

                        history.push({
                            id: `${progress.id}_${levelId}`,
                            pathId: progress.pathId,
                            pathName: path.name,
                            pathDifficulty: path.difficulty,
                            levelId: levelId,
                            levelName: level?.name || "Unknown Level",
                            creditsEarned: 0, // Individual level credits (can be customized)
                            totalPathCredits: path.creditsOnCompletion || 0,
                            earnedDate: new Date(levelStatus.lastAttemptDate),
                            type: "level_completion",
                            score: levelStatus.highestScore,
                            attempts: levelStatus.attempts
                        });
                    });

                    // Add path completion bonus (if all levels completed)
                    if (progress.pathCompleted && progress.completedAt) {
                        const allLevelsCleared = Object.values(levelStatuses).every(
                            (s) => s.cleared
                        );
                        if (allLevelsCleared) {
                            history.push({
                                id: `${progress.id}_completion`,
                                pathId: progress.pathId,
                                pathName: path.name,
                                pathDifficulty: path.difficulty,
                                levelId: "completion",
                                levelName: "Path Completion Bonus",
                                creditsEarned: path.creditsOnCompletion || 0,
                                totalPathCredits: path.creditsOnCompletion || 0,
                                earnedDate: new Date(progress.completedAt),
                                type: "path_completion",
                                score: 100,
                                attempts: Object.values(levelStatuses).length
                            });
                        }
                    }
                }

                // Sort by date (newest first)
                history.sort((a, b) => new Date(b.earnedDate) - new Date(a.earnedDate));

                setCreditHistory(history);
            } catch (error) {
                console.error("Error fetching credit history:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCreditHistory();
    }, [userProfile]);

    const getFilteredHistory = () => {
        return creditHistory.filter((item) => {
            const typeMatch =
                filterType === "all" ||
                (filterType === "level" && item.type === "level_completion") ||
                (filterType === "completion" && item.type === "path_completion");

            const pathMatch = item.pathName
                .toLowerCase()
                .includes(searchPath.toLowerCase());

            return typeMatch && pathMatch;
        });
    };

    const getDifficultyColor = (difficulty) => {
        const colors = {
            Beginner: "success",
            Intermediate: "warning",
            Advanced: "error"
        };
        return colors[difficulty] || "default";
    };

    const getDifficultyIcon = (difficulty) => {
        const icons = {
            Beginner: "🟢",
            Intermediate: "🟡",
            Advanced: "🔴"
        };
        return icons[difficulty] || "⚪";
    };

    const getTypeColor = (type) => {
        return type === "path_completion" ? "success" : "info";
    };

    const getTypeLabel = (type) => {
        return type === "path_completion" ? "Completion" : "Level Clear";
    };

    const getTypeIcon = (type) => {
        return type === "path_completion" ? "🎖️" : "✅";
    };

    const totalCreditsEarned = creditHistory.reduce(
        (sum, item) => sum + item.creditsEarned,
        0
    );

    const completionBonusCount = creditHistory.filter(
        (item) => item.type === "path_completion"
    ).length;

    const levelCompletionCount = creditHistory.filter(
        (item) => item.type === "level_completion"
    ).length;

    const filteredHistory = getFilteredHistory();

    const downloadHistory = () => {
        const headers = [
            "Date",
            "Path Name",
            "Difficulty",
            "Level/Milestone",
            "Type",
            "Credits Earned",
            "Score",
            "Attempts"
        ];

        const rows = filteredHistory.map((item) => [
            new Date(item.earnedDate).toLocaleDateString(),
            item.pathName,
            item.pathDifficulty,
            item.levelName,
            getTypeLabel(item.type),
            item.creditsEarned,
            item.score ? `${item.score}%` : "N/A",
            item.attempts || 0
        ]);

        const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `credit-history-${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
    };

    if (loading) {
        return (
            <AdminLayout title="Credit History">
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "400px"
                    }}
                >
                    <CircularProgress />
                </Box>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Credit History">
            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        💎 Credit History
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Track all your earned credits from learning paths and level completions
                    </Typography>
                </Box>

                {/* Stats Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            sx={{
                                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                color: "white",
                                borderRadius: 2
                            }}
                        >
                            <CardContent sx={{ textAlign: "center" }}>
                                <School sx={{ fontSize: 32, mb: 1 }} />
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {totalCreditsEarned}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    Total Credits
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            sx={{
                                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                                color: "white",
                                borderRadius: 2
                            }}
                        >
                            <CardContent sx={{ textAlign: "center" }}>
                                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                                    🎖️
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {completionBonusCount}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    Path Completions
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            sx={{
                                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                                color: "white",
                                borderRadius: 2
                            }}
                        >
                            <CardContent sx={{ textAlign: "center" }}>
                                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                                    ✅
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {levelCompletionCount}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    Levels Cleared
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            sx={{
                                background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                                color: "white",
                                borderRadius: 2
                            }}
                        >
                            <CardContent sx={{ textAlign: "center" }}>
                                <TrendingUp sx={{ fontSize: 32, mb: 1 }} />
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {creditHistory.length}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    Total Transactions
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Filters */}
                <Card sx={{ mb: 3, p: 2 }}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                        <TextField
                            size="small"
                            placeholder="Search by path name..."
                            value={searchPath}
                            onChange={(e) => setSearchPath(e.target.value)}
                            sx={{ flex: 1 }}
                            InputProps={{
                                startAdornment: (
                                    <FilterList sx={{ mr: 1, color: "text.secondary" }} />
                                )
                            }}
                        />
                        <TextField
                            size="small"
                            select
                            label="Filter by Type"
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            sx={{ minWidth: 150 }}
                        >
                            <MenuItem value="all">All Types</MenuItem>
                            <MenuItem value="completion">Path Completions</MenuItem>
                            <MenuItem value="level">Level Clears</MenuItem>
                        </TextField>
                        <Box
                            sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "center"
                            }}
                        >
                            <Chip
                                icon={<Download />}
                                label="Export CSV"
                                onClick={downloadHistory}
                                color="primary"
                                variant="outlined"
                            />
                        </Box>
                    </Stack>
                </Card>

                {/* History Table */}
                {filteredHistory.length === 0 ? (
                    <Alert severity="info" sx={{ textAlign: "center", py: 4 }}>
                        <Typography>
                            {creditHistory.length === 0
                                ? "No credit history yet. Complete learning paths to earn credits!"
                                : "No results match your filters. Try adjusting your search."}
                        </Typography>
                    </Alert>
                ) : (
                    <Card>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead sx={{ backgroundColor: "primary.lighter" }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Path Name</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Difficulty</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Level/Milestone</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                                            Type
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            Credits
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                                            Score
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                                            Attempts
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredHistory.map((item, index) => (
                                        <TableRow
                                            key={item.id}
                                            hover
                                            sx={{
                                                backgroundColor:
                                                    item.type === "path_completion"
                                                        ? "success.lighter"
                                                        : "inherit",
                                                "&:hover": {
                                                    backgroundColor:
                                                        item.type === "path_completion"
                                                            ? "success.lighter"
                                                            : "action.hover"
                                                }
                                            }}
                                        >
                                            <TableCell sx={{ fontWeight: 600 }}>
                                                {new Date(item.earnedDate).toLocaleDateString("en-IN", {
                                                    year: "numeric",
                                                    month: "short",
                                                    day: "numeric",
                                                    hour: "2-digit",
                                                    minute: "2-digit"
                                                })}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>
                                                {item.pathName}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={`${getDifficultyIcon(item.pathDifficulty)} ${item.pathDifficulty}`}
                                                    size="small"
                                                    color={getDifficultyColor(item.pathDifficulty)}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {item.levelName}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    icon={
                                                        <Typography
                                                            variant="caption"
                                                            sx={{ fontSize: "16px !important" }}
                                                        >
                                                            {getTypeIcon(item.type)}
                                                        </Typography>
                                                    }
                                                    label={getTypeLabel(item.type)}
                                                    size="small"
                                                    color={getTypeColor(item.type)}
                                                    variant="filled"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color:
                                                            item.creditsEarned > 0 ? "success.main" : "text.secondary"
                                                    }}
                                                >
                                                    {item.creditsEarned > 0 ? `+${item.creditsEarned}` : "-"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={item.score ? `${item.score}%` : "N/A"}
                                                    size="small"
                                                    color={
                                                        item.score >= 70
                                                            ? "success"
                                                            : item.score >= 50
                                                                ? "warning"
                                                                : "error"
                                                    }
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {item.attempts || 0}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Summary Footer */}
                        <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
                            <Stack
                                direction={{ xs: "column", md: "row" }}
                                justifyContent="space-between"
                                spacing={2}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    Showing {filteredHistory.length} of {creditHistory.length} transactions
                                </Typography>
                                <Box sx={{ display: "flex", gap: 3 }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Credits in this view
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 700,
                                                color: "success.main"
                                            }}
                                        >
                                            +{filteredHistory.reduce((sum, item) => sum + item.creditsEarned, 0)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Stack>
                        </Box>
                    </Card>
                )}

                {/* Legend */}
                <Card sx={{ mt: 3, p: 2, backgroundColor: "info.lighter" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        📌 Credit Types Explained
                    </Typography>
                    <Stack spacing={1}>
                        <Typography variant="caption">
                            <strong>✅ Level Clear:</strong> Awarded when you successfully complete a level
                            with score ≥ passing score
                        </Typography>
                        <Typography variant="caption">
                            <strong>🎖️ Path Completion:</strong> Bonus awarded when you complete all levels
                            in a learning path
                        </Typography>
                        <Typography variant="caption">
                            <strong>💡 Total Credits:</strong> Sum of all path completion bonuses earned
                        </Typography>
                    </Stack>
                </Card>
            </Container>
        </AdminLayout>
    );
}
